import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import React from "react";

import { getOpsPallets, type PalletInfo } from "@/api/warehouse_ops";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { BottomSheet } from "@/lib/scan-ui/overlays";
import { LoadState } from "@/lib/scan-ui/screen";
import { detailColumns, detailValue } from "@/lib/warehouse-ops";

import { RefetchError } from "./RefetchError";

/** Собранные в портале паллеты для хранения: фактом, без сигнала отгрузки,
 *  не применённые (`GET …/ops/pallets`). */
export function PalletsSheet({
  dateFrom,
  dateTo,
  onClose,
}: {
  dateFrom: string;
  dateTo: string;
  onClose: () => void;
}): React.ReactElement {
  const q = useQuery({
    queryKey: ["warehouse-ops-pallets", dateFrom, dateTo],
    queryFn: () => getOpsPallets(dateFrom, dateTo),
  });
  const columns = detailColumns("", "pallet");
  return (
    <BottomSheet onClose={onClose} panelClassName="max-h-[85dvh] overflow-hidden flex flex-col">
      <div className="flex items-start justify-between gap-2 border-b p-3">
        <div>
          <div className="text-[15px] font-semibold">Собранные паллеты для хранения</div>
          <div className="text-xs text-muted-foreground">
            {fmtDate(dateFrom)} — {fmtDate(dateTo)}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Закрыть">
          <X className="size-4" />
        </Button>
      </div>
      <div className="space-y-3 overflow-auto p-3">
        {q.data && q.error && <RefetchError error={q.error} onRetry={() => void q.refetch()} />}
        {!q.data ? (
          <LoadState isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <>
            <Group
              title="Стоят фактом (отгружены позже сборки: скан в портале или проведение документа в 1С)"
              rows={q.data.standing}
              columns={columns}
            />
            <Group
              title="Без сигнала отгрузки (ни скана, ни проведённого документа) — фактом не считаются, товар в остатке по норме"
              rows={q.data.no_signal}
              columns={columns}
              warn
            />
            <Group
              title="Стояли, но не применены"
              rows={q.data.not_applied}
              columns={[...columns, { key: "reason_title", title: "Почему" }]}
              warn
            />
            <Group
              title="Отгружены в день сборки — дата документа в 1С раньше сборки (не стояли ни дня)"
              rows={q.data.doc_before_assembly}
              columns={[...columns, { key: "posted_at", title: "Дата документа" }]}
              warn
            />
            <Group
              title="Сессии в другой базе 1С (реплика) — не считаются"
              rows={q.data.other_base}
              columns={columns}
            />
          </>
        )}
      </div>
    </BottomSheet>
  );
}

function Group({
  title,
  rows,
  columns,
  warn,
}: {
  title: string;
  rows: PalletInfo[];
  columns: { key: string; title: string }[];
  warn?: boolean;
}): React.ReactElement {
  return (
    <section aria-label={title}>
      <div className={warn && rows.length ? "mb-1 text-xs font-semibold text-amber-800" : "mb-1 text-xs font-semibold"}>
        {title} · {rows.length}
      </div>
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted-foreground">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="py-1 pr-2 font-medium">
                    {c.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t align-top">
                  {columns.map((c) => (
                    <td key={c.key} className="py-1 pr-2">
                      {detailValue(c.key, r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
